import produce from 'immer';
import { DraftStatus, ModelStatus, SATOStatus, SetDraftResponse } from 'api/Responses/SetResponses';
import { SetAction } from './draftActions';

// Quantities are stored as strings
const addQty = (a: string, b: string): string => String((Number(a) || 0) + (Number(b) || 0));

export const initialSet: SetDraftResponse = {
  code: '',
  display_code: '',
  revision: '',
  region: '',
  jme_group: '',
  trays: {},
  status: DraftStatus.UNTOUCHED,
  model_status: ModelStatus.NOT_LIVE,
  sato_status: SATOStatus.NOT_FOUND,
  set_manuals: { manual_japanese: undefined, manual_english: undefined },
  owner_id: '',
  owner_email: '',
  name: '',
  user_id: '',
  draft_exists: false,
  owned_by: '',
  is_view_only: true,
  savi_version: 0,
  tenant: '',
};

const setReducer = produce((draft: SetDraftResponse, action: SetAction) => {
  switch (action.type) {
    // Initialises the draft in state
    case 'INIT':
      Object.assign(draft, action.payload);
      break;
    // Updates the set code (unique to the set)
    case 'CODE':
      draft.code = action.payload;
      break;
    // Updates the set name
    case 'NAME':
      draft.name = action.payload;
      break;
    // Updates the set region
    case 'REGION':
      draft.region = action.payload;
      break;
    // Updates the set group description
    case 'JME':
      draft.jme_group = action.payload;
      break;
    // Updates the set revision number
    case 'REV':
      draft.revision = action.payload;
      break;
    // Updates the set manual
    case 'SET_MANUAL':
      if (action.payload.region === 'JPN') {
        draft.set_manuals.manual_japanese = action.payload.filename;
      } else {
        draft.set_manuals.manual_english = action.payload.filename;
      }
      break;
    // Updates the tray image
    case 'TRAY_IMAGE':
      draft.trays[action.trayID].image_file = action.payload.image;
      draft.trays[action.trayID].original_image_file = action.payload.originalImage;
      draft.trays[action.trayID].reference_tags_metadata = action.payload.referenceTags;
      break;
    // Updates the tray reference number
    case 'TRAY_REF':
      draft.trays[action.trayID].reference = action.payload;
      break;
    // Updates the name of the tray
    case 'TRAY_NAME':
      draft.trays[action.trayID].name = action.payload;
      break;
    // Updates the tray non standard attribute
    case 'TRAY_NON_STANDARD':
      draft.trays[action.trayID].non_standard = action.payload;
      break;
    // Updates the tray general test attribute
    case 'TRAY_GENERAL_TEST':
      draft.trays[action.trayID].general_test = action.payload;
      break;
    // Adds a tray to the set, using the tray reference (unique to the tray)
    case 'TRAY_NEW':
      draft.trays[action.payload.reference] = action.payload;
      break;
    // Deletes a tray from the set
    case 'TRAY_DELETE':
      delete draft.trays[action.trayID];
      break;
    // Updates the additional inspection requirements for a tray
    case 'TRAY_ADDITIONAL_INSPECTION':
      draft.trays[action.trayID].additional_inspection_requirements = action.payload;
      break;
    // Updates the part reference number within the tray
    case 'PART_REF':
      draft.trays[action.trayID].parts[action.partID].ref = action.payload;
      break;
    // Updates the quantity of the given part within the tray
    case 'PART_QTY':
      draft.trays[action.trayID].parts[action.partID].qty = action.payload;
      break;
    // Creates a new part by adding it to the specified tray, using the part code (unique to the part)
    case 'PART_NEW': {
      const parts = draft.trays[action.trayID].parts;
      const existing = parts[action.payload.code];
      // A code already in the tray is merged (the user confirmed): quantities add up, the existing row is kept
      if (existing) existing.qty = addQty(existing.qty, action.payload.qty);
      else parts[action.payload.code] = action.payload;
      break;
    }
    // Deletes all the given parts from the tray
    case 'PART_DELETE_MANY':
      action.partIDs.forEach(id => delete draft.trays[action.trayID].parts[id]);
      break;
    // Moves parts between trays unchanged; a code already in the target tray has its quantity added to the existing row
    case 'PART_MOVE': {
      if (action.fromTrayID === action.toTrayID) break;
      const from = draft.trays[action.fromTrayID].parts;
      const to = draft.trays[action.toTrayID].parts;
      action.partIDs.forEach(id => {
        if (!from[id]) return;
        if (to[id]) to[id].qty = addQty(to[id].qty, from[id].qty);
        else to[id] = from[id];
        delete from[id];
      });
      break;
    }
    // Deletes the part from the tray
    case 'PART_DELETE':
      delete draft.trays[action.trayID].parts[action.partID];
      break;
  }
});

export default setReducer;
